import { PageHeader } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import GoalsCharts from './components/GoalsCharts'
import styled from 'styled-components'
import useFilterParamsGeneric from 'hooks/useFilterParamsGeneric'
import { FilterValues } from './types'
import dayjs from 'dayjs'
import { useGoalsDashboard } from './hooks/useGoalsDashboard'
import { DashboardFilters } from 'screens/Reports/MonitoringReports'
import SelectedFiltersDisplay from 'screens/Reports/MonitoringReports/components/DashboardFilters/SelectedFiltersDisplay'
import { goalReportsConstants } from 'locale/goalReports'
import useResidents from 'hooks/useFindItem/useResidents'
import { getName } from 'screens/Todos/components/BasicFilters'
import { Button } from '@lmc/ui-kit/src/general'
import { FilePdfOutlined } from '@ant-design/icons'
import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Home } from '@lmc/ts-common'
import usePdfExport from 'hooks/usePdfExport'
import jsPDF from 'jspdf'
import logo from '../../../../images/LMC-Logo-Pos-RGB 1.png'
import { LoadingScreen } from 'components/generic'
import { commonPatterns } from '../../../../utils/enhancedTestIds'

// substracting 5 as 1 will be the current month
const initialFiltersValue: FilterValues = {
  rangePicker: [dayjs().subtract(5, 'month').startOf('month'), dayjs()],
  resident: undefined
}

const GoalsDashboard = ({ testId }: { testId?: string } = {}) => {
  const { t } = useTranslation(NAMESPACE.GOAL_REPORTS)

  const { filterParamsGeneric, setFilterParams } =
    useFilterParamsGeneric(initialFiltersValue)

  const filterParams = filterParamsGeneric as FilterValues

  const { data, isLoading } = useGoalsDashboard(filterParams as FilterValues)

  const onFilterChange = (value: FilterValues) => {
    const newValue = { ...value }
    setFilterParams((prevFilters) => ({
      ...prevFilters,
      ...newValue
    }))
  }

  const filterKeyTitle = {
    rangePicker: t(goalReportsConstants.FILTER_DATE),
    resident: t(goalReportsConstants.FILTER_RESIDENT)
  }

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const { all: allResidents } = useResidents({})

  const resident = allResidents?.find((r) => r.id === filterParams.resident)
  const residentName = resident ? getName(resident) : null
  const selectedFilters = {
    ...filterParams,
    ...(filterParams.resident ? { resident: [residentName] } : {})
  }

  const [isDownloading, setIsDownloading] = useState(false)

  const pdfContent = useRef<HTMLInputElement>(null)

  const generatePDFHeader = (pdf: jsPDF) => {
    pdf.setFont('Helvetica')
    pdf.setFontSize(24)
    pdf.text(`${t(goalReportsConstants.GOALS_DASHBOARD)} - PDF`, 10, 20)
    pdf.addImage(logo, 'JPEG', 170, 15, 140 / 4, 25 / 4)
    pdf.setDrawColor('#D9D9D9')
    pdf.setLineWidth(0.25)
    pdf.line(10, 25, 220, 25)
    pdf.setFontSize(18)
    pdf.text(`${t(goalReportsConstants.SITE)}: ${home.name}`, 10, 35)
  }

  const handleDownloadPdf = usePdfExport(
    pdfContent,
    `${t(goalReportsConstants.GOALS_DASHBOARD)}:${home.name}`,
    setIsDownloading,
    1,
    generatePDFHeader,
    35
  )

  const baseId = testId || 'goals-dashboard'

  return (
    <GoalsDashboardWrapper data-testid={baseId}>
      <PageHeader
        title={
          <TitleContainer>
            {t(goalReportsConstants.GOALS_DASHBOARD)}
          </TitleContainer>
        }
        data-testid={`${baseId}-header`}
      >
        <Button
          onClick={handleDownloadPdf}
          isLoading={isDownloading}
          data-testid={commonPatterns.button(baseId, 'download-pdf')}
        >
          <FilePdfOutlined />
          {t(goalReportsConstants.DOWNLOAD_PDF)}
        </Button>
      </PageHeader>
      <DashboardFilters
        onChange={onFilterChange}
        value={filterParams}
        withLoggedBy={false}
        variant="highContrast"
        hasResidentsFilter={false}
        picker="month"
        hasSingleResident
        data-testid={`${baseId}-filters`}
      />
      <GoalsDashboardCharts ref={pdfContent} data-testid={`${baseId}-charts`}>
        <SelectedFiltersDisplay
          selectedFilters={selectedFilters}
          filterKeyTitle={filterKeyTitle}
          dateFormat="MM/YYYY"
          data-testid={`${baseId}-selected-filters`}
        />
        <GoalsCharts
          chartsData={data}
          dateRange={filterParams.rangePicker}
          resident={filterParams.resident}
          isLoading={isLoading}
          testId={baseId}
        />
      </GoalsDashboardCharts>
    </GoalsDashboardWrapper>
  )
}

const GoalsDashboardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 24px 50px;
`

const GoalsDashboardCharts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 50px;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

export default GoalsDashboard
